import type { ContentData } from "@/api/content";
import { useLang } from "@/i18n/LanguageContext";

/** Returns an embeddable iframe src for YouTube/Vimeo, or null for direct files. */
function toEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith("/embed/")) return url;
      return null;
    }
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

export function VideoView({ content }: { content: ContentData }) {
  const { t } = useLang();
  const url = content.url ?? "";
  if (!url) return <p className="no-data-message">{t("Geen video.", "No video.")}</p>;
  const embed = toEmbedUrl(url);

  return (
    <div className="video-embed">
      <div className="video-frame">
        {embed ? (
          <iframe
            src={embed}
            title={content.caption || "video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video src={url} controls />
        )}
      </div>
      {content.caption && <p className="video-caption">{content.caption}</p>}
    </div>
  );
}
